/**
 * Cursor-based pagination primitives (API-09).
 *
 * Mobile lists ALWAYS use cursor pagination — never offset. Servers MUST
 * accept `cursor` (opaque string token returned by the previous page) and
 * `limit` (1..200, default 50), and MUST respond with a `nextCursor` that is
 * either an opaque continuation token or `null` when the list is exhausted.
 *
 * The cursor format is server-private; the mobile client treats it as
 * opaque. Servers typically encode `(createdAt, id)` or `(updatedAt, id)`.
 */
import { z } from "zod";

export const DEFAULT_PAGE_LIMIT = 50;
export const MAX_PAGE_LIMIT = 200;

export const CursorPaginationRequest = z
  .object({
    cursor: z
      .string()
      .optional()
      .describe(
        "Opaque continuation token from the previous response's `nextCursor`. Omit on the first request.",
      ),
    limit: z
      .number()
      .int()
      .min(1)
      .max(MAX_PAGE_LIMIT)
      .default(DEFAULT_PAGE_LIMIT)
      .describe(
        `Maximum items to return. Server clamps to [1, ${MAX_PAGE_LIMIT}]. Defaults to ${DEFAULT_PAGE_LIMIT}.`,
      ),
  })
  .strict();

export type CursorPaginationRequest = z.infer<typeof CursorPaginationRequest>;

/**
 * Wraps an item schema in a paginated-response envelope.
 *
 * Use at the route layer:
 *   const ProjectListResponse = CursorPaginatedResponse(ProjectSchema);
 */
export function CursorPaginatedResponse<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    items: z.array(itemSchema),
    nextCursor: z
      .string()
      .nullable()
      .describe(
        "Pass back as `cursor` on the next request. `null` when this is the last page.",
      ),
  });
}

/**
 * Server-side variant of the envelope above, with `.strict()` re-applied.
 *
 * The client-facing factory is deliberately NOT strict (issue #2411): this
 * envelope is parsed by the MOBILE APP — `SupervisorProjectsApi` builds it at
 * `apps/mobile/lib/api/supervisorProjects.ts` — and an installed build parses
 * with the schema it was compiled against. A strict envelope would turn the
 * next field added here (`total`, `hasMore`, …) into a hard parse failure on
 * every device already in the field, rather than a key they ignore.
 *
 * Strictness is still worth having at the SERVER response boundary, where it
 * catches an over-serialised row before it reaches the wire, so every route
 * that parses its own response uses this variant instead. Tolerance is a
 * client property; the guard is a server one.
 *
 * `.strict()` applies only to the object it is called on, so strictening the
 * envelope alone would leave each ITEM exactly as permissive as the client
 * schema passed in — and a serialiser that gained a column would be silently
 * stripped with a 200 instead of failing loudly (Codex, #2427 round 6: the
 * signatures list carried the newly permissive `SignatureSchema` while the
 * detail route parsed `SignatureSchema.strict()`). So an object item is made
 * strict here too. That reaches one level; an item with its own permissive
 * nested objects needs an explicit full-depth variant, the way
 * `ToolboxLogResponseStrict` is built.
 */
export function CursorPaginatedResponseStrict<T extends z.ZodTypeAny>(
  itemSchema: T,
) {
  // Same inferred output type as `T` — `.strict()` changes what is REJECTED,
  // not what a successful parse produces — so the cast is type-honest.
  const item = (
    itemSchema instanceof z.ZodObject ? itemSchema.strict() : itemSchema
  ) as T;
  return CursorPaginatedResponse(item).strict();
}

/**
 * Type helper for a paginated response of a given item type. Use when you
 * want the inferred TypeScript type without instantiating the Zod schema
 * (e.g. for the mobile fetch wrapper return type).
 */
export type CursorPaginatedResponseOf<T> = {
  items: T[];
  nextCursor: string | null;
};
